import { Dayjs } from "dayjs";
import { UserPayload } from "../models/SiteModel";
import { apiFetch } from "./fetchService";

const URLAPI = process.env.REACT_APP_BASE_URL;

export type HistorialPreciosPage = {
    filas: Array<Record<string, unknown>>;
    total: number;
    pagina: number;
    tamanoPagina: number;
};

export async function fetchHistorialPrecios(
    user: UserPayload,
    idLocal: number,
    fechaDesde: Dayjs,
    fechaHasta: Dayjs,
    pagina: number,
    tamanoPagina: number
): Promise<HistorialPreciosPage> {
    const params = new URLSearchParams({
        idLocal: String(idLocal),
        fechaDesde: fechaDesde.format("YYYY-MM-DD"),
        fechaHasta: fechaHasta.format("YYYY-MM-DD"),
        pagina: String(pagina),
        tamanoPagina: String(tamanoPagina),
    });
    return apiFetch(user.token, `${URLAPI}/Producto/ReporteHistorialPrecios?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
    }) as Promise<HistorialPreciosPage>;
}

export async function fetchGetLocales(user: UserPayload) {
    const token = user.token; // await GetToken(msalcon);
    const response = apiFetch(token, `${URLAPI}/Reportes/GetLocales`, 
        {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }});

    const data = await response;
    return data;
}

export async function fetchGetCajas(user: UserPayload, idLocal:string, fecha_desde: Dayjs, fecha_hasta: Dayjs) {
    const token = user.token; // await GetToken(msalcon);
    const dataToPost = {
        idLocal: idLocal,
        fechaDesde: fecha_desde.toJSON(),
        fechaHasta: fecha_hasta.toJSON()
    };
    const response = await fetch(
        `${URLAPI}/Reportes/GetCajas`,
        {
            method: "POST", headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify(dataToPost)
        }
    );

    const data = await response.json();
    return data;
}

export async function fetchGetMovimientos(user: UserPayload, idLocal:string, idCaja: string, pv:string, cajaAbierta: boolean) {
    const token = user.token; // await GetToken(msalcon);
    const dataToPost = {
        idLocal: idLocal,
        idCaja: idCaja,
        pv: pv,
        cajaAbierta: cajaAbierta
    };
    const response = await fetch(
        `${URLAPI}/Reportes/GetMovimientos`,
        {
            method: "POST", headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify(dataToPost)
        }
    );

    const data = await response.json();
    return data;
}

export async function fetchGetReporteVentas(
    user: UserPayload,
    fecha_desde: Dayjs, fecha_hasta: Dayjs, id_local: number) {

    const token = user.token; // await GetToken(msalcon);
    const dataToPost = {
        fechaDesde: fecha_desde.toJSON(),
        fechaHasta: fecha_hasta.toJSON(),
        idLocal: id_local
    };
    const response = await fetch(
        `${URLAPI}/Reportes/GetReporteVentas`,
        {
            method: "POST", headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify(dataToPost)
        }
    );

    const data = await response.json();
    return data;
}

export async function fetchGetReporteEstadosDeCuentasClientes(id_local: string, user: UserPayload) {
    const token = user.token; // await GetToken(msalcon);
    const response = await fetch(
        `${URLAPI}/Cliente/GetEstadosDeCuentasCorrientesLocal/${id_local}`,
        {
            method: "GET", headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        }
    );

    const data = await response.json();
    return data;
}

export async function fetchGetEstadosCuentaProveedores(user: UserPayload) {
    const response = await fetch(`${URLAPI}/Proveedor/GetEstadosDeCuenta`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + user.token }
    });
    if (!response.ok) throw new Error("No se pudo cargar el estado de cuentas de proveedores.");
    return response.json();
}

export async function fetchGetDetalleCuentaProveedor(user: UserPayload, idProveedor: number) {
    const response = await fetch(`${URLAPI}/Proveedor/GetEstadoCuenta/${idProveedor}`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + user.token }
    });
    if (!response.ok) throw new Error("No se pudo cargar el detalle de la cuenta del proveedor.");
    return response.json();
}

export type ReporteStockValorizadoData = {
    idLocal: number;
    cantidadProductos: number;
    unidadesTotales: number;
    valorizacionCompra: number;
    valorizacionCompraSinIva: number;
    costoPromedio: number;
    rubros: { idRubro: number; rubro: string; valorizacionCompra: number; cantidadProductos: number }[];
    detalle: { idProducto: number; producto: string; cantidad: number; precioCompra: number; valorizacion: number }[];
    totalDetalle: number;
    page: number;
    pageSize: number;
};

export async function fetchGetReporteStockValorizado(user: UserPayload, idLocal: string, page: number, pageSize: number, signal?: AbortSignal): Promise<ReporteStockValorizadoData> {
    const response = await fetch(`${URLAPI}/Reportes/GetStockValorizado/${idLocal}?page=${page}&pageSize=${pageSize}`, {
        method: "GET",
        signal,
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + user.token }
    });
    if (!response.ok) throw new Error("No se pudo cargar la valorización de stock.");
    return response.json();
}

export type VentaDetalleItem = {
    producto: string;
    cantidad: number;
    forma: string;
    cantidadForma: number;
    unidadesPorForma: number;
    cantidadBase: number;
    precioUnitario: number;
    precio: number;
};

export async function fetchGetDetalleVenta(user: UserPayload, idVenta: number, idLocal: number, proforma: boolean) {
    const params = new URLSearchParams({ idVenta: String(idVenta), idLocal: String(idLocal), proforma: String(proforma) });
    const response = await fetch(`${URLAPI}/Reportes/GetDetalleVenta?${params.toString()}`, {
        method: "GET",
        headers: { Authorization: "Bearer " + user.token },
    });
    if (!response.ok) throw new Error(response.statusText || `HTTP ${response.status}`);

    const data = await response.json();
    const rows = Array.isArray(data) ? data : data?.$values ?? data?.rows ?? data?.Rows ?? [];
    return rows.map((row: any): VentaDetalleItem => ({
        producto: row.PRODUCTO ?? row.producto ?? "",
        cantidad: Number(row.CANTIDAD ?? row.cantidad ?? 0),
        forma: row.FORMA ?? row.forma ?? "Sin forma registrada",
        cantidadForma: Number(row.CANTIDADFORMA ?? row.cantidadForma ?? 0),
        unidadesPorForma: Number(row.UNIDADESPORFORMA ?? row.unidadesPorForma ?? 0),
        cantidadBase: Number(row.CANTIDADBASE ?? row.cantidadBase ?? 0),
        precioUnitario: Number(row["PRECIO UNITARIO"] ?? row.precioUnitario ?? 0),
        precio: Number(row.PRECIO ?? row.precio ?? 0),
    }));
}

export type AuditoriaStockItem = {
    operacion: string;
    fecha: string;
    hora: string;
    producto: string;
    descripcion: string;
    stockAntes: number;
    cantidad: number;
    stockDespues: number;
    forma: string;
    cantidadForma: number;
    unidadesPorForma: number;
    origen: string;
    idOrigen: number;
    usuario: string;
};

export async function fetchGetAuditoriaStock(
    user: UserPayload,
    idLocal: number,
    fechaDesde: string,
    fechaHasta: string,
    idProducto?: number,
    signal?: AbortSignal
): Promise<AuditoriaStockItem[]> {
    const params = new URLSearchParams({ idLocal: String(idLocal), fechaDesde, fechaHasta });
    if (idProducto) params.set("idProducto", String(idProducto));
    const response = await fetch(`${URLAPI}/Reportes/GetAuditoriaStock?${params.toString()}`, {
        method: "GET",
        headers: { Authorization: "Bearer " + user.token },
        signal,
    });
    if (!response.ok) throw new Error(response.statusText || `HTTP ${response.status}`);

    const data = await response.json();
    const rows = Array.isArray(data) ? data : data?.$values ?? data?.rows ?? data?.Rows ?? [];
    return rows.map((row: any): AuditoriaStockItem => ({
        operacion: row.OPERACION ?? row.operacion ?? "",
        fecha: row.FECHA ?? row.fecha ?? "",
        hora: row.HORA ?? row.hora ?? "",
        producto: row.PRODUCTO ?? row.producto ?? "",
        descripcion: row.DESCRIPCION ?? row.descripcion ?? "",
        stockAntes: Number(row["STOCK ANTES"] ?? row.stockAntes ?? 0),
        cantidad: Number(row.CANTIDAD ?? row.cantidad ?? 0),
        stockDespues: Number(row["STOCK DESPUES"] ?? row.stockDespues ?? 0),
        forma: row.FORMA ?? row.forma ?? "Sin forma registrada",
        cantidadForma: Number(row["CANTIDAD FORMA"] ?? row.cantidadForma ?? 0),
        unidadesPorForma: Number(row["UNIDADES POR FORMA"] ?? row.unidadesPorForma ?? 0),
        origen: row.ORIGEN ?? row.origen ?? "",
        idOrigen: Number(row["ID ORIGEN"] ?? row.idOrigen ?? 0),
        usuario: row.USUARIO ?? row.usuario ?? "",
    }));
}

export type ReporteStockPorLocalItem = {
    idProducto: number;
    codigo: string;
    producto: string;
    stock: number;
    descripcion: string;
    presentacion: string;
    precioContado: number;
    precioMayor: number;
    precioMenor: number;
    precioCompra: number;
    idRubro: number;
    rubro: string;
    activo: boolean;
    estado: string;
};

export type ReporteStockPorLocalPage = {
    items: ReporteStockPorLocalItem[];
    total: number;
    page: number;
    pageSize: number;
};

export async function fetchGetReporteStockPorLocal(
    user: UserPayload,
    idLocal: string,
    page: number,
    pageSize: number,
    filters: {
        search?: string;
        idRubro?: number;
        incluirDesactivados?: boolean;
        comparadorStock?: string;
        cantidadStock?: number;
    },
    signal?: AbortSignal
): Promise<ReporteStockPorLocalPage> {
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        incluirDesactivados: String(Boolean(filters.incluirDesactivados)),
    });
    if (filters.search?.trim()) params.set("search", filters.search.trim());
    if (filters.idRubro) params.set("idRubro", String(filters.idRubro));
    if (filters.comparadorStock) params.set("comparadorStock", filters.comparadorStock);
    if (filters.cantidadStock !== undefined && Number.isFinite(filters.cantidadStock)) {
        params.set("cantidadStock", String(filters.cantidadStock));
    }

    const response = await fetch(`${URLAPI}/Reportes/GetStockPorLocal/${idLocal}?${params.toString()}`, {
        method: "GET",
        signal,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + user.token
        }
    });

    if (!response.ok) {
        throw new Error(response.statusText || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
        items: data.items ?? data.Items ?? [],
        total: data.total ?? data.Total ?? 0,
        page: data.page ?? data.Page ?? page,
        pageSize: data.pageSize ?? data.PageSize ?? pageSize,
    };
}

export async function fetchExportReporteStockPorLocalTodo(
    user: UserPayload,
    idLocal: string,
    incluirDesactivados: boolean
): Promise<Blob> {
    const params = new URLSearchParams({
        incluirDesactivados: String(incluirDesactivados),
    });

    const response = await fetch(`${URLAPI}/Reportes/ExportStockPorLocalTodo/${idLocal}?${params.toString()}`, {
        method: "GET",
        headers: {
            'Authorization': 'Bearer ' + user.token
        }
    });

    if (!response.ok) {
        throw new Error(response.statusText || `HTTP ${response.status}`);
    }

    return response.blob();
}
